import { Stack, Typography } from "@mui/material";
import React from "react";
import { DividerComponent } from "../../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import {
  Button,
  IconButton,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { EmailCycleBasicInformationForm } from "../../forms/EmailCycleBasicInformationForm";
import { observer } from "mobx-react";
import { useCreateEmailCycleStore } from "../../../stores/Hooks";
import { EmailCycleMessageCustomizationForm } from "../../forms/EmailCycleMessageCustomizationForm";
import { ScheduleType } from "../../../stores/CreateEmailCycleStore";
import { useEmailTemplateDetailsStore } from "../../../../emailTemplates/stores/Hooks";

interface CreateEmailCycleDrawerContentProps {
  onClose: VoidFunction;
  onScheduleEmail: (scheduleType: ScheduleType) => void;
}

export const CreateEmailCycleDrawerContent: React.FC<CreateEmailCycleDrawerContentProps> =
  observer(({ onClose, onScheduleEmail }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const tokens = useFoundationColorTokens();
    const typography = useTypography();

    const emailTemplateDetailsStore = useEmailTemplateDetailsStore();
    const createEmailCycleStore = useCreateEmailCycleStore();

    const disableActionButtons =
      createEmailCycleStore.disableScheduleEmail ||
      emailTemplateDetailsStore.loadingEmailTemplateDetails;

    return (
      <Stack
        divider={<DividerComponent orientation="horizontal" width="100%" />}
        height="100%"
      >
        {/* Drawer Header */}
        <Stack
          direction="row"
          paddingBlock={spacing.spaceMD}
          paddingInline={spacing.space2XL}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography color={tokens.labelHighEmphasis} {...typography.sh2}>
            {t("emailCycles.createEmailCycle")}
          </Typography>
          <IconButton
            name="createEmailTemplateDrawerCloseButton"
            size="medium"
            icon={<X />}
            variant="plain-neutral"
            onClick={onClose}
          />
        </Stack>

        {/* Drawer Body */}
        <Stack
          padding={spacing.space2XL}
          flex={1}
          gap={spacing.spaceXL}
          overflow="auto"
          divider={<DividerComponent orientation="horizontal" width="100%" />}
        >
          <EmailCycleBasicInformationForm
            cycleName={createEmailCycleStore.emailCycleName}
            fromEmailId={createEmailCycleStore.senderEmailId}
            fromEmailIdApplicationErrorMessage={
              createEmailCycleStore.senderEmailIdRPCError
                ? t(
                    "emailCycles.fromEmailDomainNotAllowedErrorMessage",
                    "The sender email domain is not allowed",
                  )
                : undefined
            }
            onFromEmailIdChange={createEmailCycleStore.setSenderEmailId}
            displayName={createEmailCycleStore.senderName}
            subject={createEmailCycleStore.emailContent.subject}
          />
          <EmailCycleMessageCustomizationForm
            templateId={createEmailCycleStore.templateId}
            setTemplateId={createEmailCycleStore.setTemplateId}
            templateName={createEmailCycleStore.templateName}
            templateContent={createEmailCycleStore.emailContent.body}
            enableRTEPopupZIndexFix
          />
        </Stack>

        {/* Drawer Footer */}
        <Stack
          direction="row"
          justifyContent="end"
          paddingBlock={spacing.spaceMD}
          paddingInline={spacing.space2XL}
          gap={spacing.spaceSM}
        >
          <Button
            name="setRecurringScheduleButton"
            label={t("emailCycles.recurringScheduleEmail")}
            variant="plain-neutral"
            size="medium"
            onClick={() => onScheduleEmail("RECURRING_SCHEDULE")}
            disabled={disableActionButtons}
          />
          <Button
            name="sendEmailButton"
            label={t("emailCycles.sendEmail")}
            variant="filled"
            size="medium"
            onClick={() => onScheduleEmail("SINGLE_TIME_SCHEDULE")}
            disabled={disableActionButtons}
          />
        </Stack>
      </Stack>
    );
  });
